import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';

const DEFAULT_SCALE = 0.003;

export function EarthModel({ scale = DEFAULT_SCALE, position = [0, 0, 0]}) {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const modelRef = useRef(null);
  const animationIdRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;

    // Create scene
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // Create camera
    const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
    camera.position.z = 5;
    cameraRef.current = camera;

    // Create renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Add lights - much brighter lighting for visibility
    const ambientLight = new THREE.AmbientLight(0xffffff, 1.5);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 2.5);
    directionalLight.position.set(5, 3, 5);
    scene.add(directionalLight);

    const backLight = new THREE.DirectionalLight(0xffffff, 1.0);
    backLight.position.set(-5, -3, -5);
    scene.add(backLight);

    const topLight = new THREE.DirectionalLight(0xffffff, 1.0);
    topLight.position.set(0, 10, 0);
    scene.add(topLight);

    // Load textures separately
    const textureLoader = new THREE.TextureLoader();
    
    // Load Earth textures - converted to JPG/PNG format
    const earthColorTexture = textureLoader.load('/earth_color_43K.jpg');
    const cloudsTexture = textureLoader.load('/earth_clouds_8K.jpg');
    // Configure textures
    earthColorTexture.colorSpace = THREE.SRGBColorSpace;
    earthColorTexture.anisotropy = 16;
    
    cloudsTexture.colorSpace = THREE.SRGBColorSpace;
    cloudsTexture.anisotropy = 16;

    // Load GLB model
    const loader = new GLTFLoader();
    loader.load(
      '/earth3.glb',
      (gltf) => {
        const nodes = {};
        const materials = {};

        // Extract nodes and materials
        gltf.scene.traverse((child) => {
          if (child.isMesh) {
            nodes[child.name] = child;
            if (child.material) {
              materials[child.material.name] = child.material;
            }
          }
        });

        // Get materials
        const atmosphereMaterial = materials['Material.003'];

        // Use externally loaded textures instead of GLB materials
        const earthTextureMap = earthColorTexture;
        const cloudsTextureMap = cloudsTexture;

        // Create group to hold all meshes
        const earthGroup = new THREE.Group();
        earthGroup.scale.set(scale, scale, scale);
        earthGroup.position.set(position[0], position[1], position[2]);
        modelRef.current = earthGroup;

        // Create Earth surface mesh with manual material
        if (nodes.surface) {
          const surfaceGeometry = nodes.surface.geometry.clone();
          
          // Ensure smooth normals - more aggressive approach
          if (surfaceGeometry.attributes.normal) {
            surfaceGeometry.deleteAttribute('normal');
          }
          surfaceGeometry.computeVertexNormals();

          const surfaceMaterial = new THREE.MeshStandardMaterial({
            map: earthTextureMap,
            metalness: 0.0,
            roughness: 0.5,
            flatShading: false,
            side: THREE.FrontSide,
            color: 0xffffff,
            emissive: 0x000000,
            emissiveIntensity: 0
          });

          const surfaceMesh = new THREE.Mesh(surfaceGeometry, surfaceMaterial);
          surfaceMesh.rotation.copy(nodes.surface.rotation);
          surfaceMesh.position.copy(nodes.surface.position);
          earthGroup.add(surfaceMesh);
        
        }

        // Create clouds mesh with manual material
        if (nodes.claud) {
          const cloudsGeometry = nodes.claud.geometry.clone();
          
          // Ensure smooth normals
          if (cloudsGeometry.attributes.normal) {
            cloudsGeometry.deleteAttribute('normal');
          }
          cloudsGeometry.computeVertexNormals();

          const cloudsMaterial = new THREE.MeshStandardMaterial({
            map: cloudsTextureMap,
            transparent: true,
            opacity: 0.5,
            depthWrite: false,
            flatShading: false,
            side: THREE.DoubleSide,
            color: 0xffffff
          });

          const cloudsMesh = new THREE.Mesh(cloudsGeometry, cloudsMaterial);
          cloudsMesh.rotation.copy(nodes.claud.rotation);
          cloudsMesh.position.copy(nodes.claud.position);
          earthGroup.add(cloudsMesh);
        }

        // Create atmosphere mesh
        if (nodes.atmosphere && atmosphereMaterial) {
          const atmosphereGeometry = nodes.atmosphere.geometry.clone();
          atmosphereGeometry.computeVertexNormals();

          const atmosphereMesh = new THREE.Mesh(
            atmosphereGeometry,
            atmosphereMaterial
          );

          atmosphereMesh.rotation.copy(nodes.atmosphere.rotation);
          atmosphereMesh.position.copy(nodes.atmosphere.position);
          earthGroup.add(atmosphereMesh);
        }

        scene.add(earthGroup);
      },
      undefined,
      (error) => {
        console.error('Error loading Earth model:', error);
      }
    );

    // Animation loop
    const animate = () => {
      animationIdRef.current = requestAnimationFrame(animate);

      if (modelRef.current) {
        modelRef.current.rotation.y += 0.002;
      }

      renderer.render(scene, camera);
    };
    animate();

    // Handle resize
    const handleResize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
      if (container && renderer.domElement && container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      renderer.dispose();
      if (modelRef.current) {
        scene.remove(modelRef.current);
      }
    };
  }, [scale, position]);

  return (
    <div 
      ref={containerRef} 
      style={{ 
        width: '100%', 
        height: '78vh',

        margin: 0, 

      }}
    />
  );
}